// ============================================================
// CANONICAL BOOK CATALOG — single source of truth
// All pages displaying book cards should import from here.
// ============================================================

export interface BookCatalogEntry {
  id: string
  title: string
  author: string
  authorId?: string
  coverImage: string
  rating: number
  reviewCount?: number
  formats: string[]
  genres: string[]
  subgenres?: string[]
  // ── New Structured Genre Fields ────────────────────────────
  primaryGenre?: string
  mainSubgenre?: string
  secondaryGenres?: string[]
  subgenreType?: 'core' | 'crossover' | 'microgenre'
  displayLabel?: string
  shortLabel?: string
  refinements?: string[]
  // ── Structured tag IDs (reference data/bookTags.ts) ──────
  genreId?: string
  subgenreId?: string
  tropeIds?: string[]
  themeIds?: string[]
  characterIds?: string[]
  settingId?: string
  representationIds?: string[]
  relationshipTypeId?: string
  pairingType?: string // e.g. 'MF', 'MM', 'FF', 'Why Choose'
  tropes?: string[] // e.g. ['Enemies to Lovers', 'Forced Proximity']
  burnLevel?: number // 1-3 scale (Slow Burn/Medium Burn/Fast Burn)
  // ── Writing Style ────────────────────────────────────────
  pov?: string // POV type id from bookTags.ts povTypes
  tense?: string // Tense type id from bookTags.ts tenseTypes
  // ── Anthology support ────────────────────────────────────
  isAnthology?: boolean
  contributors?: Array<{
    authorName: string
    authorSlug: string
    storyTitle: string
    pages?: number
    // Per-story metadata for anthology detail page
    pov?: string // e.g. 'First Person', 'Dual POV', 'Third Person Limited'
    tense?: string // e.g. 'Past Tense', 'Present Tense'
    spiceLevel?: number // 1-5
    darknessLevel?: number // 1-5
    contentWarnings?: string[]
    tropes?: string[] // e.g. ['Enemies to Lovers', 'Forced Proximity']
    subgenres?: string[] // e.g. ['Dark Romance', 'Mafia Romance']
    fullBookId?: string // if this story was expanded into a standalone novel
    fullBookTitle?: string
  }>
  // ── Series Crossover support ─────────────────────────────
  crossoverSeries?: Array<{
    seriesName: string
    seriesSlug: string
    readingOrder?: number // position in combined reading order
  }>
  // ── Existing fields (kept for backward compat) ───────────
  isNew?: boolean
  isDebut?: boolean
  spiceLevel: number
  darknessLevel: number
  endingType?: string
  seriesName?: string
  seriesNumber?: number
  totalSeriesBooks?: number
  seriesStatus?: 'ongoing' | 'complete'
  characterRelationships?: Array<{
    type: string
    style?: string[]
    characters?: string
  }>
  contentWarnings?: string[]
  tags?: string[]
  releaseDate?: string
}

// ... existing code ...

// ── Example books (spec-compliant, fully relational) ─────────

export const exampleBooks: BookCatalogEntry[] = [
  {
    id: 'example-1',
    title: 'Small Town Rivals',
    author: 'Example Author',
    coverImage:
      'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.5,
    reviewCount: 0,
    formats: ['ebook', 'paperback'],
    genres: ['Romance'],
    subgenres: ['Contemporary Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Contemporary Romance',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Contemporary Romance',
    shortLabel: 'Contemp Romance',
    // Structured relations
    genreId: 'romance',
    subgenreId: 'contemporary-romance',
    tropeIds: ['enemies-to-lovers', 'forced-proximity'],
    themeIds: ['redemption'],
    characterIds: ['strong-female-lead', 'brooding-hero'],
    settingId: 'small-town',
    representationIds: [],
    relationshipTypeId: 'mf',
    burnLevel: 1,
    spiceLevel: 2,
    darknessLevel: 1,
    endingType: 'HEA',
    contentWarnings: [],
    tags: ['enemies-to-lovers', 'forced-proximity', 'small-town', 'redemption'],
    releaseDate: '2024-01-01',
  },
  {
    id: 'example-2',
    title: 'Blood Debt',
    author: 'Example Author',
    coverImage:
      'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.3,
    reviewCount: 0,
    formats: ['ebook', 'paperback'],
    genres: ['Romance'],
    subgenres: ['Dark Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Dark Romance',
    secondaryGenres: ['Thriller'],
    subgenreType: 'crossover',
    displayLabel: 'Dark Romance',
    shortLabel: 'Dark Romance',
    // Structured relations
    genreId: 'romance',
    subgenreId: 'dark-romance',
    tropeIds: ['revenge-romance', 'forced-proximity'],
    themeIds: ['identity'],
    characterIds: ['strong-female-lead', 'morally-grey-antihero'],
    settingId: 'big-city',
    representationIds: ['lgbtq'],
    relationshipTypeId: 'mf',
    burnLevel: 3,
    spiceLevel: 4,
    darknessLevel: 4,
    endingType: 'HFN',
    contentWarnings: ['Violence', 'Kidnapping', 'Sexual Content'],
    tags: [
      'revenge-romance',
      'forced-proximity',
      'big-city',
      'identity',
      'dark-romance',
    ],
    releaseDate: '2024-02-01',
  },
  {
    id: 'example-3',
    title: 'Crown of Ash',
    author: 'Example Author',
    coverImage:
      'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.6,
    reviewCount: 0,
    formats: ['ebook', 'hardcover'],
    genres: ['Fantasy'],
    subgenres: ['Epic Fantasy'],
    primaryGenre: 'Fantasy',
    mainSubgenre: 'Epic Fantasy',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Epic Fantasy',
    shortLabel: 'Epic Fantasy',
    // Structured relations
    genreId: 'fantasy',
    subgenreId: 'epic-fantasy',
    tropeIds: ['chosen-one'],
    themeIds: ['coming-of-age'],
    characterIds: ['reluctant-hero'],
    settingId: 'fantasy-world',
    representationIds: ['bipoc'],
    relationshipTypeId: 'none',
    burnLevel: undefined,
    spiceLevel: 0,
    darknessLevel: 2,
    endingType: 'Cliffhanger',
    contentWarnings: ['Violence'],
    tags: ['chosen-one', 'coming-of-age', 'fantasy-world', 'bipoc'],
    releaseDate: '2024-03-01',
  },
  {
    id: 'example-4',
    title: 'The Quiet Witness',
    author: 'Example Author',
    coverImage:
      'https://images.unsplash.com/photo-1610882648335-ced8fc8fa6b6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.4,
    reviewCount: 0,
    formats: ['ebook', 'paperback'],
    genres: ['Thriller'],
    subgenres: ['Psychological Thriller'],
    primaryGenre: 'Thriller',
    mainSubgenre: 'Psychological Thriller',
    secondaryGenres: ['Mystery'],
    subgenreType: 'core',
    displayLabel: 'Psychological Thriller',
    shortLabel: 'Psych Thriller',
    // Structured relations
    genreId: 'thriller',
    subgenreId: 'psychological-thriller',
    tropeIds: ['unreliable-narrator'],
    themeIds: ['grief-and-loss'],
    characterIds: ['unreliable-narrator-char'],
    settingId: 'isolated-mansion',
    representationIds: ['neurodivergent'],
    relationshipTypeId: 'none',
    burnLevel: undefined,
    spiceLevel: 0,
    darknessLevel: 3,
    endingType: 'Bittersweet',
    contentWarnings: ['Violence', 'Addiction'],
    tags: [
      'unreliable-narrator',
      'grief-and-loss',
      'isolated-mansion',
      'neurodivergent',
    ],
    releaseDate: '2024-04-01',
  },
]

// ── Convenience slices (used by Home, Browse carousels, etc.) ──

export const allBooks: BookCatalogEntry[] = [
  // ── Elena Richards ──────────────────────────────────────────
  {
    id: '1',
    title: 'The Silent Echo',
    author: 'Elena Richards',
    authorId: 'elena-richards',
    coverImage:
      'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.7,
    reviewCount: 312,
    formats: ['paperback', 'ebook', 'audiobook'],
    genres: ['Mystery', 'Thriller'],
    primaryGenre: 'Thriller',
    mainSubgenre: 'Psychological Thriller',
    secondaryGenres: ['Mystery'],
    subgenreType: 'core',
    displayLabel: 'Psychological Thriller',
    shortLabel: 'Psych Thriller',
    isNew: true,
    spiceLevel: 3,
    darknessLevel: 4,
    endingType: 'HEA',
    tropes: ['Enemies to Lovers', 'Slow Burn'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'dual-pov',
    tense: 'past-tense',
    seriesName: 'Echo Series',
    seriesNumber: 1,
    totalSeriesBooks: 3,
    seriesStatus: 'ongoing',
    crossoverSeries: [
      {
        seriesName: 'Shadow Files',
        seriesSlug: 'shadow-files',
        readingOrder: 2,
      },
    ],
    characterRelationships: [
      {
        type: 'Enemies to Lovers',
        style: ['Male & Female (MF)', 'Slow Burn'],
        characters: 'Mira Hayes & Detective Ryan Cole',
      },
    ],
    contentWarnings: ['Violence', 'Strong Language'],
    tags: [
      'enemies-to-lovers',
      'slow-burn-trope',
      'small-town',
      'plot-twists',
      'strong-female-lead',
    ],
    releaseDate: '2022-03-15',
  },
  {
    id: '2',
    title: 'The Broken Echo',
    author: 'Elena Richards',
    authorId: 'elena-richards',
    coverImage:
      'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.9,
    reviewCount: 278,
    formats: ['paperback', 'ebook', 'audiobook'],
    genres: ['Mystery', 'Thriller'],
    primaryGenre: 'Thriller',
    mainSubgenre: 'Psychological Thriller',
    secondaryGenres: ['Mystery'],
    subgenreType: 'core',
    displayLabel: 'Psychological Thriller',
    shortLabel: 'Psych Thriller',
    spiceLevel: 4,
    darknessLevel: 4,
    endingType: 'Cliffhanger',
    tropes: ['Enemies to Lovers', 'Slow Burn'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'dual-pov',
    tense: 'past-tense',
    seriesName: 'Echo Series',
    seriesNumber: 2,
    totalSeriesBooks: 3,
    seriesStatus: 'ongoing',
    crossoverSeries: [
      {
        seriesName: 'Shadow Files',
        seriesSlug: 'shadow-files',
        readingOrder: 4,
      },
    ],
    characterRelationships: [
      { type: 'Enemies to Lovers', style: ['Male & Female (MF)', 'Slow Burn'] },
    ],
    contentWarnings: ['Violence', 'Strong Language'],
    tags: ['enemies-to-lovers', 'slow-burn-trope', 'small-town', 'plot-twists'],
    releaseDate: '2022-09-20',
  },
  {
    id: '3',
    title: 'Whispers in the Dark',
    author: 'Elena Richards',
    authorId: 'elena-richards',
    coverImage:
      'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.8,
    reviewCount: 198,
    formats: ['paperback', 'ebook'],
    genres: ['Horror', 'Supernatural'],
    primaryGenre: 'Horror',
    mainSubgenre: 'Supernatural Horror',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Supernatural Horror',
    shortLabel: 'Supernatural',
    spiceLevel: 2,
    darknessLevel: 5,
    endingType: 'Bittersweet',
    tropes: ['Forbidden Love'],
    pairingType: 'MF',
    burnLevel: 2,
    pov: 'first-person',
    tense: 'present-tense',
    characterRelationships: [
      { type: 'Forbidden Love', style: ['Male & Female (MF)'] },
    ],
    contentWarnings: ['Violence', 'Paranormal', 'Grief & Loss'],
    tags: ['paranormal', 'small-town', 'grief-loss', 'found-identity'],
    releaseDate: '2021-10-31',
  },
  // ── Marcus Chen ─────────────────────────────────────────────
  {
    id: '4',
    title: 'Beyond the Veil',
    author: 'Marcus Chen',
    authorId: 'marcus-chen',
    coverImage:
      'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.5,
    reviewCount: 145,
    formats: ['ebook', 'audiobook'],
    genres: ['Fantasy', 'Young Adult'],
    primaryGenre: 'Fantasy',
    mainSubgenre: 'YA Fantasy',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'YA Fantasy',
    shortLabel: 'YA',
    spiceLevel: 1,
    darknessLevel: 2,
    endingType: 'HEA',
    tropes: ['Friends to Lovers'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'third-person-limited',
    tense: 'past-tense',
    characterRelationships: [
      { type: 'Friends to Lovers', style: ['Slow Burn'] },
    ],
    tags: ['magic', 'found-family', 'coming-of-age', 'fantasy-world'],
    releaseDate: '2022-05-22',
  },
  {
    id: '5',
    title: 'Heart of the Storm',
    author: 'Marcus Chen',
    authorId: 'marcus-chen',
    coverImage:
      'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.7,
    reviewCount: 112,
    formats: ['ebook', 'hardcover', 'audiobook'],
    genres: ['Fantasy', 'Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Fantasy Romance',
    secondaryGenres: ['Fantasy'],
    subgenreType: 'crossover',
    displayLabel: 'Fantasy Romance',
    shortLabel: 'Fant Romance',
    spiceLevel: 4,
    darknessLevel: 3,
    endingType: 'HEA',
    tropes: ['Enemies to Lovers', 'Slow Burn'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'alternating-pov',
    tense: 'past-tense',
    seriesName: 'Elements Trilogy',
    seriesNumber: 1,
    totalSeriesBooks: 3,
    seriesStatus: 'ongoing',
    characterRelationships: [
      { type: 'Enemies to Lovers', style: ['Slow Burn'] },
    ],
    tags: [
      'enemies-to-lovers',
      'magic',
      'slow-burn-trope',
      'found-family',
      'fantasy-world',
      'bipoc',
    ],
    releaseDate: '2022-05-22',
  },
  // ── Linked editions example ────────────────────────────────
  // ONE catalogue listing for a story that has two linked editions. The
  // edition-specific metadata lives in data/linkedEditions.ts; the values
  // below mirror the DEFAULT (Light) edition so any surface that has not been
  // made edition-aware still shows something accurate.
  {
    id: 'ember-and-ash',
    title: 'Ember and Ash',
    author: 'Elena Richards',
    authorId: 'elena-richards',
    coverImage:
      'https://images.unsplash.com/photo-1490750967868-88aa4486c946?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.6,
    reviewCount: 84,
    formats: ['ebook', 'paperback', 'audiobook'],
    genres: ['Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Small Town Romance',
    subgenreType: 'microgenre',
    displayLabel: 'Small Town Romance',
    shortLabel: 'Small Town',
    spiceLevel: 3,
    darknessLevel: 2,
    endingType: 'HEA',
    tropes: ['Second Chance', 'Returning Home', 'Slow Burn'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'alternating-pov',
    tense: 'past-tense',
    characterRelationships: [
      { type: 'Second Chance Romance', style: ['Slow Burn'] },
    ],
    contentWarnings: ['Grief', 'House Fire', 'Estranged Family'],
    tags: ['second-chance', 'small-town', 'slow-burn-trope', 'found-family'],
    releaseDate: '2024-03-12',
  },
  {
    id: '6',
    title: 'Soul of Fire',
    author: 'Marcus Chen',
    authorId: 'marcus-chen',
    coverImage:
      'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.8,
    reviewCount: 89,
    formats: ['ebook', 'hardcover', 'audiobook'],
    genres: ['Fantasy', 'Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Fantasy Romance',
    secondaryGenres: ['Fantasy'],
    subgenreType: 'crossover',
    displayLabel: 'Fantasy Romance',
    shortLabel: 'Fant Romance',
    spiceLevel: 4,
    darknessLevel: 3,
    endingType: 'HEA',
    tropes: ['Forbidden Love', 'Slow Burn'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'alternating-pov',
    tense: 'past-tense',
    seriesName: 'Elements Trilogy',
    seriesNumber: 2,
    totalSeriesBooks: 3,
    seriesStatus: 'ongoing',
    characterRelationships: [{ type: 'Forbidden Love', style: ['Slow Burn'] }],
    tags: [
      'slow-burn-trope',
      'magic',
      'redemption',
      'found-family',
      'fantasy-world',
      'bipoc',
    ],
    releaseDate: '2022-07-10',
  },
  // ── Sarah Johnson ────────────────────────────────────────────
  {
    id: '7',
    title: 'Midnight Shadows',
    author: 'Sarah Johnson',
    authorId: 'sarah-johnson',
    coverImage:
      'https://images.unsplash.com/photo-1515876305430-f06edab8282a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.0,
    reviewCount: 63,
    formats: ['hardcover', 'ebook', 'audiobook'],
    genres: ['Supernatural', 'Thriller'],
    primaryGenre: 'Thriller',
    mainSubgenre: 'Supernatural Thriller',
    secondaryGenres: ['Horror'],
    subgenreType: 'crossover',
    displayLabel: 'Supernatural Thriller',
    shortLabel: 'Supernatural',
    spiceLevel: 2,
    darknessLevel: 4,
    tropes: ['Forced Proximity', 'Slow Burn'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'first-person',
    tense: 'past-tense',
    characterRelationships: [
      { type: 'Forced Proximity', style: ['Slow Burn'] },
    ],
    contentWarnings: ['Violence', 'Paranormal'],
    tags: [
      'small-town',
      'redemption',
      'violence',
      'paranormal',
      'forced-proximity',
      'grief-loss',
    ],
    releaseDate: '2022-01-15',
  },
  // ── Amara Knight ─────────────────────────────────────────────
  {
    id: '8',
    title: 'Starfall',
    author: 'Amara Knight',
    authorId: 'amara-knight',
    coverImage:
      'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.9,
    reviewCount: 203,
    formats: ['paperback', 'ebook'],
    genres: ['Sci-Fi', 'Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Sci-Fi Romance',
    secondaryGenres: ['Science Fiction'],
    subgenreType: 'crossover',
    displayLabel: 'Sci-Fi Romance',
    shortLabel: 'Sci-Fi',
    isNew: true,
    spiceLevel: 4,
    darknessLevel: 2,
    endingType: 'HEA',
    tropes: ['Slow Burn', 'Forced Proximity'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'dual-pov',
    tense: 'present-tense',
    characterRelationships: [
      { type: 'Slow Burn', style: ['Male & Female (MF)'] },
    ],
    tags: ['slow-burn-trope', 'forced-proximity', 'found-family'],
    releaseDate: '2023-01-10',
  },
  // ── David Williams ───────────────────────────────────────────
  {
    id: '9',
    title: 'The Lost City',
    author: 'David Williams',
    authorId: 'david-williams',
    coverImage:
      'https://images.unsplash.com/photo-1541963463532-d68292c34b19?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.3,
    reviewCount: 87,
    formats: ['hardcover', 'ebook', 'audiobook'],
    genres: ['Adventure', 'Historical Fiction'],
    primaryGenre: 'Historical Fiction',
    mainSubgenre: 'Historical Adventure',
    secondaryGenres: ['Adventure'],
    subgenreType: 'crossover',
    displayLabel: 'Historical Adventure',
    shortLabel: 'Hist Adventure',
    spiceLevel: 2,
    darknessLevel: 3,
    endingType: 'HEA',
    burnLevel: 2,
    pov: 'third-person-omniscient',
    tense: 'past-tense',
    tags: ['coming-of-age', 'redemption', 'fantasy-world'],
    releaseDate: '2021-08-05',
  },
  // ── Thomas Lee ───────────────────────────────────────────────
  {
    id: '10',
    title: 'The Hidden Truth',
    author: 'Thomas Lee',
    authorId: 'thomas-lee',
    coverImage:
      'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.2,
    reviewCount: 54,
    formats: ['ebook'],
    genres: ['Mystery', 'Crime'],
    primaryGenre: 'Thriller',
    mainSubgenre: 'Crime Thriller',
    secondaryGenres: ['Mystery'],
    subgenreType: 'core',
    displayLabel: 'Crime Thriller',
    shortLabel: 'Crime Thriller',
    spiceLevel: 2,
    darknessLevel: 3,
    burnLevel: 2,
    pov: 'first-person',
    tense: 'past-tense',
    tags: ['plot-twists', 'strong-female-lead'],
    releaseDate: '2022-11-01',
  },
  // ── Zoe Parker ───────────────────────────────────────────────
  {
    id: '11',
    title: 'Echoes of Tomorrow',
    author: 'Zoe Parker',
    authorId: 'zoe-parker',
    coverImage:
      'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.6,
    reviewCount: 76,
    formats: ['hardcover', 'ebook'],
    genres: ['Sci-Fi', 'Dystopian'],
    primaryGenre: 'Science Fiction',
    mainSubgenre: 'Dystopian Sci-Fi',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Dystopian Sci-Fi',
    shortLabel: 'Dystopian',
    isNew: true,
    spiceLevel: 2,
    darknessLevel: 3,
    endingType: 'Bittersweet',
    burnLevel: 2,
    pov: 'first-person',
    tense: 'present-tense',
    tags: ['coming-of-age', 'found-identity', 'chosen-one'],
    releaseDate: '2023-02-14',
  },
  // ── Finn Murphy ──────────────────────────────────────────────
  {
    id: '12',
    title: 'The Last Spell',
    author: 'Finn Murphy',
    authorId: 'finn-murphy',
    coverImage:
      'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.8,
    reviewCount: 134,
    formats: ['paperback', 'ebook', 'audiobook'],
    genres: ['Fantasy', 'Magic'],
    primaryGenre: 'Fantasy',
    mainSubgenre: 'High Fantasy',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'High Fantasy',
    shortLabel: 'High Fantasy',
    isNew: true,
    spiceLevel: 3,
    darknessLevel: 2,
    endingType: 'HEA',
    tropes: ['Friends to Lovers', 'Slow Burn'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'third-person-limited',
    tense: 'past-tense',
    characterRelationships: [
      { type: 'Friends to Lovers', style: ['Slow Burn'] },
    ],
    tags: ['magic', 'found-family', 'chosen-one', 'slow-burn-trope'],
    releaseDate: '2023-03-01',
  },
  // ── Maya Rodriguez ───────────────────────────────────────────
  {
    id: '13',
    title: 'Beneath the Surface',
    author: 'Maya Rodriguez',
    authorId: 'maya-rodriguez',
    coverImage:
      'https://images.unsplash.com/photo-1610882648335-ced8fc8fa6b6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.7,
    reviewCount: 98,
    formats: ['paperback', 'ebook'],
    genres: ['Thriller', 'Psychological'],
    primaryGenre: 'Thriller',
    mainSubgenre: 'Psychological Thriller',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Psychological Thriller',
    shortLabel: 'Psych Thriller',
    isNew: true,
    spiceLevel: 3,
    darknessLevel: 4,
    endingType: 'Bittersweet',
    burnLevel: 3,
    pov: 'multi-pov',
    tense: 'mixed-tense',
    contentWarnings: ['Violence', 'Psychological'],
    tags: [
      'strong-female-lead',
      'neurodivergent',
      'plot-twists',
      'found-identity',
      'bipoc',
    ],
    releaseDate: '2023-04-05',
  },
  // ── Liam Bennett ─────────────────────────────────────────────
  {
    id: '14',
    title: 'The Forgotten Path',
    author: 'Liam Bennett',
    authorId: 'liam-bennett',
    coverImage:
      'https://images.unsplash.com/photo-1612969308146-066027504792?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.5,
    reviewCount: 61,
    formats: ['hardcover', 'ebook', 'audiobook'],
    genres: ['Adventure', 'Coming of Age'],
    primaryGenre: 'Adventure',
    mainSubgenre: 'Adventure',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Adventure',
    shortLabel: 'Adventure',
    refinements: ['Coming of Age'],
    isNew: true,
    spiceLevel: 1,
    darknessLevel: 2,
    endingType: 'HEA',
    burnLevel: 2,
    pov: 'third-person-limited',
    tense: 'past-tense',
    tags: ['coming-of-age', 'found-family', 'found-identity'],
    releaseDate: '2023-05-20',
  },
  // ── Jessica Martinez ─────────────────────────────────────────
  {
    id: '15',
    title: 'Broken Promises',
    author: 'Jessica Martinez',
    authorId: 'jessica-martinez',
    coverImage:
      'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.3,
    reviewCount: 89,
    formats: ['paperback', 'ebook'],
    genres: ['Contemporary', 'Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Contemporary Romance',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Contemporary Romance',
    shortLabel: 'Contemp Romance',
    spiceLevel: 3,
    darknessLevel: 2,
    endingType: 'HEA',
    tropes: ['Friends to Lovers', 'Fake Dating'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'first-person',
    tense: 'present-tense',
    characterRelationships: [
      { type: 'Friends to Lovers', style: ['Slow Burn'] },
    ],
    tags: [
      'friends-to-lovers',
      'strong-female-lead',
      'small-town',
      'coming-of-age',
      'fake-dating',
      'slow-burn-trope',
    ],
    releaseDate: '2022-02-14',
  },
  // ── Michael Torres ───────────────────────────────────────────
  {
    id: '16',
    title: 'The Last Guardian',
    author: 'Michael Torres',
    authorId: 'michael-torres',
    coverImage:
      'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.6,
    reviewCount: 94,
    formats: ['hardcover', 'ebook', 'audiobook'],
    genres: ['Urban Fantasy', 'Paranormal'],
    primaryGenre: 'Fantasy',
    mainSubgenre: 'Urban Fantasy',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Urban Fantasy',
    shortLabel: 'Urban Fantasy',
    spiceLevel: 2,
    darknessLevel: 3,
    endingType: 'HEA',
    tropes: ['Slow Burn', 'Found Family'],
    pairingType: 'MM',
    burnLevel: 1,
    pov: 'first-person',
    tense: 'past-tense',
    characterRelationships: [{ type: 'Slow Burn', style: ['Found Family'] }],
    tags: [
      'brooding-hero',
      'anti-hero',
      'found-family',
      'lgbtq',
      'magic',
      'redemption',
      'found-identity',
      'bipoc',
    ],
    releaseDate: '2021-11-05',
  },
  // ── Emily Watson ─────────────────────────────────────────────
  {
    id: '17',
    title: 'Summer in Paris',
    author: 'Emily Watson',
    authorId: 'emily-watson',
    coverImage:
      'https://images.unsplash.com/photo-1558901357-ca41e027e43a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.4,
    reviewCount: 76,
    formats: ['paperback', 'ebook'],
    genres: ['Contemporary', 'Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Contemporary Romance',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Contemporary Romance',
    shortLabel: 'Contemp Romance',
    spiceLevel: 3,
    darknessLevel: 1,
    endingType: 'HEA',
    tropes: ['Friends to Lovers', 'Fake Dating'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'first-person',
    tense: 'present-tense',
    characterRelationships: [
      { type: 'Friends to Lovers', style: ['Slow Burn'] },
    ],
    tags: [
      'big-city',
      'friends-to-lovers',
      'slow-burn-trope',
      'coming-of-age',
      'fake-dating',
    ],
    releaseDate: '2022-06-01',
  },
  // ── Alexander Wright ─────────────────────────────────────────
  {
    id: '18',
    title: 'The Forgotten Kingdom',
    author: 'Alexander Wright',
    authorId: 'alexander-wright',
    coverImage:
      'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.7,
    reviewCount: 103,
    formats: ['hardcover', 'ebook', 'audiobook'],
    genres: ['Historical', 'Fantasy'],
    primaryGenre: 'Fantasy',
    mainSubgenre: 'Historical Fantasy',
    secondaryGenres: ['Historical Fiction'],
    subgenreType: 'crossover',
    displayLabel: 'Historical Fantasy',
    shortLabel: 'Hist Fantasy',
    spiceLevel: 2,
    darknessLevel: 4,
    endingType: 'Bittersweet',
    tropes: ['Enemies to Lovers', 'Slow Burn'],
    pairingType: 'MF',
    burnLevel: 1,
    pov: 'third-person-omniscient',
    tense: 'past-tense',
    seriesName: 'Lost Kingdoms',
    seriesNumber: 1,
    totalSeriesBooks: 3,
    seriesStatus: 'ongoing',
    characterRelationships: [
      { type: 'Enemies to Lovers', style: ['Slow Burn'] },
    ],
    contentWarnings: ['Violence'],
    tags: [
      'enemies-to-lovers',
      'rivals-to-lovers',
      'brooding-hero',
      'anti-hero',
      'chosen-one',
      'redemption',
      'violence',
      'fantasy-world',
    ],
    releaseDate: '2021-09-20',
  },
  // ── Rachel Kim ───────────────────────────────────────────────
  {
    id: '19',
    title: 'Beneath the Surface',
    author: 'Rachel Kim',
    authorId: 'rachel-kim',
    coverImage:
      'https://images.unsplash.com/photo-1551029506-0807df4e2031?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.9,
    reviewCount: 132,
    formats: ['paperback', 'ebook', 'audiobook'],
    genres: ['Psychological', 'Thriller'],
    primaryGenre: 'Thriller',
    mainSubgenre: 'Psychological Thriller',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Psychological Thriller',
    shortLabel: 'Psych Thriller',
    spiceLevel: 2,
    darknessLevel: 5,
    endingType: 'Bittersweet',
    burnLevel: 3,
    pov: 'alternating-pov',
    tense: 'past-tense',
    contentWarnings: ['Violence', 'Psychological'],
    tags: [
      'strong-female-lead',
      'neurodivergent',
      'plot-twists',
      'sexual-content',
      'found-identity',
      'bipoc',
    ],
    releaseDate: '2022-04-05',
  },
  // ── David Chen ───────────────────────────────────────────────
  {
    id: '20',
    title: 'Starlight Melody',
    author: 'David Chen',
    authorId: 'david-chen',
    coverImage:
      'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.5,
    reviewCount: 84,
    formats: ['hardcover', 'ebook'],
    genres: ['Science Fiction', 'Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Sci-Fi Romance',
    secondaryGenres: ['Science Fiction'],
    subgenreType: 'crossover',
    displayLabel: 'Sci-Fi Romance',
    shortLabel: 'Sci-Fi',
    spiceLevel: 3,
    darknessLevel: 2,
    endingType: 'HEA',
    tropes: ['Slow Burn', 'Found Family'],
    pairingType: 'MM',
    burnLevel: 1,
    pov: 'dual-pov',
    tense: 'past-tense',
    characterRelationships: [{ type: 'Slow Burn', style: ['Found Family'] }],
    tags: [
      'slow-burn-trope',
      'lgbtq',
      'found-family',
      'neurodivergent',
      'forced-proximity',
      'bipoc',
    ],
    releaseDate: '2022-08-12',
  },
  // ── Debut authors ────────────────────────────────────────────
  {
    id: 'debut1',
    title: 'Whispers of the Forgotten',
    author: 'Maya Collins',
    authorId: 'maya-collins',
    coverImage:
      'https://images.unsplash.com/photo-1603162525937-92a06da4b8ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 0,
    formats: ['paperback', 'ebook'],
    genres: ['Fantasy'],
    primaryGenre: 'Fantasy',
    mainSubgenre: 'Epic Fantasy',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Epic Fantasy',
    shortLabel: 'Epic Fantasy',
    isNew: true,
    isDebut: true,
    spiceLevel: 2,
    darknessLevel: 2,
    burnLevel: 2,
    pov: 'third-person-limited',
    tense: 'past-tense',
    tags: ['magic', 'found-family', 'coming-of-age'],
    releaseDate: '2023-06-01',
  },
  {
    id: 'debut2',
    title: 'The Last Lighthouse Keeper',
    author: 'Thomas Reed',
    authorId: 'thomas-reed',
    coverImage:
      'https://images.unsplash.com/photo-1580934738404-5eb8d4c0389e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 0,
    formats: ['ebook'],
    genres: ['Historical Fiction'],
    primaryGenre: 'Historical Fiction',
    mainSubgenre: 'Historical Literary',
    secondaryGenres: ['Literary Fiction'],
    subgenreType: 'core',
    displayLabel: 'Historical Literary',
    shortLabel: 'Literary',
    isNew: true,
    isDebut: true,
    spiceLevel: 1,
    darknessLevel: 2,
    pov: 'first-person',
    tense: 'past-tense',
    tags: ['small-town', 'grief-loss', 'coming-of-age'],
    releaseDate: '2023-06-15',
  },
  {
    id: 'debut3',
    title: 'Parallel Hearts',
    author: 'Sophia Chen',
    authorId: 'sophia-chen',
    coverImage:
      'https://images.unsplash.com/photo-1544084944-15c3893308fb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 3.5,
    formats: ['paperback', 'ebook'],
    genres: ['Romance'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Contemporary Romance',
    secondaryGenres: [],
    subgenreType: 'core',
    displayLabel: 'Contemporary Romance',
    shortLabel: 'Contemp Romance',
    isNew: true,
    isDebut: true,
    spiceLevel: 3,
    darknessLevel: 1,
    endingType: 'HEA',
    pov: 'dual-pov',
    tense: 'present-tense',
    characterRelationships: [{ type: 'Enemies to Lovers' }],
    tags: ['enemies-to-lovers', 'fake-dating'],
    releaseDate: '2023-07-01',
  },
  {
    id: 'debut4',
    title: 'Midnight Protocol',
    author: 'James Wilson',
    authorId: 'james-wilson',
    coverImage:
      'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.1,
    formats: ['ebook', 'audiobook'],
    genres: ['Thriller', 'Sci-Fi'],
    primaryGenre: 'Science Fiction',
    mainSubgenre: 'Science Fiction Thriller',
    secondaryGenres: ['Thriller'],
    subgenreType: 'crossover',
    displayLabel: 'Sci-Fi Thriller',
    shortLabel: 'SF Thriller',
    isNew: true,
    isDebut: true,
    spiceLevel: 2,
    darknessLevel: 3,
    pov: 'third-person-limited',
    tense: 'past-tense',
    contentWarnings: ['Violence'],
    tags: ['plot-twists', 'anti-hero'],
    releaseDate: '2023-07-15',
  },
  {
    id: 'debut5',
    title: "The Botanist's Daughter",
    author: 'Emma Lawson',
    authorId: 'emma-lawson',
    coverImage:
      'https://images.unsplash.com/photo-1580933073521-dc49ac0d4e6a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 0,
    formats: ['paperback', 'ebook'],
    genres: ['Mystery', 'Literary Fiction'],
    primaryGenre: 'Mystery',
    mainSubgenre: 'Literary Mystery',
    secondaryGenres: ['Literary Fiction'],
    subgenreType: 'crossover',
    displayLabel: 'Literary Mystery',
    shortLabel: 'Lit Mystery',
    isNew: true,
    isDebut: true,
    spiceLevel: 1,
    darknessLevel: 2,
    pov: 'first-person',
    tense: 'past-tense',
    tags: ['small-town', 'coming-of-age', 'found-identity'],
    releaseDate: '2023-08-01',
  },
  // ── Anthology example ────────────────────────────────────────
  {
    id: 'anthology-1',
    title: 'Shadows & Seduction: A Dark Romance Anthology',
    author: 'Various Authors',
    coverImage:
      'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    rating: 4.6,
    reviewCount: 156,
    formats: ['paperback', 'ebook'],
    genres: ['Romance', 'Thriller'],
    primaryGenre: 'Romance',
    mainSubgenre: 'Dark Romance',
    secondaryGenres: ['Thriller'],
    subgenreType: 'crossover',
    displayLabel: 'Dark Romance',
    shortLabel: 'Dark Romance',
    refinements: ['Anthology'],
    spiceLevel: 4,
    darknessLevel: 4,
    endingType: 'HEA',
    isAnthology: true,
    contributors: [
      {
        authorName: 'Elena Richards',
        authorSlug: 'elena-richards',
        storyTitle: 'The Debt Collector',
        pages: 45,
        pov: 'Dual POV',
        tense: 'Past Tense',
        spiceLevel: 4,
        darknessLevel: 5,
        contentWarnings: ['Kidnapping', 'Dubious Consent', 'Violence'],
        tropes: ['Enemies to Lovers', 'Forced Proximity', 'Captive Romance'],
        subgenres: ['Dark Romance', 'Mafia Romance'],
        fullBookId: '1',
        fullBookTitle: 'The Silent Echo',
      },
      {
        authorName: 'Jessica Martinez',
        authorSlug: 'jessica-martinez',
        storyTitle: 'Stolen Vows',
        pages: 52,
        pov: 'First Person',
        tense: 'Present Tense',
        spiceLevel: 4,
        darknessLevel: 3,
        contentWarnings: ['Arranged Marriage', 'Emotional Manipulation'],
        tropes: ['Arranged Marriage', 'Enemies to Lovers', 'Fake Dating'],
        subgenres: ['Dark Romance', 'Billionaire Romance'],
      },
      {
        authorName: 'Rachel Kim',
        authorSlug: 'rachel-kim',
        storyTitle: 'Midnight Bargain',
        pages: 38,
        pov: 'Alternating POV',
        tense: 'Past Tense',
        spiceLevel: 5,
        darknessLevel: 4,
        contentWarnings: ['Power Imbalance', 'Explicit Content'],
        tropes: ['Boss/Employee', 'Age Gap', 'Forbidden Romance'],
        subgenres: ['Dark Romance', 'Office Romance'],
      },
      {
        authorName: 'Amara Knight',
        authorSlug: 'amara-knight',
        storyTitle: 'Gravity of Sin',
        pages: 41,
        pov: 'Dual POV',
        tense: 'Present Tense',
        spiceLevel: 4,
        darknessLevel: 4,
        contentWarnings: ['Violence', 'Morally Grey Characters'],
        tropes: ['Rivals to Lovers', 'Forced Proximity', 'Touch Her and Die'],
        subgenres: ['Dark Romance', 'Suspense Romance'],
      },
      {
        authorName: 'Emily Watson',
        authorSlug: 'emily-watson',
        storyTitle: 'The Arrangement',
        pages: 48,
        pov: 'First Person',
        tense: 'Past Tense',
        spiceLevel: 3,
        darknessLevel: 2,
        contentWarnings: ['Mild Angst'],
        tropes: ['Contract Relationship', 'Slow Burn', 'Grumpy/Sunshine'],
        subgenres: ['Contemporary Romance', 'Billionaire Romance'],
      },
    ],
    contentWarnings: ['Violence', 'Sexual Content', 'Kidnapping'],
    tags: [
      'dark-romance',
      'enemies-to-lovers',
      'forced-proximity',
      'revenge-romance',
    ],
    releaseDate: '2023-10-15',
  },
]

export const newReleaseBooks = allBooks.filter((b) => b.isNew)

export const debutBooks = allBooks.filter((b) => b.isDebut)

export const bestSellerBooks = [...allBooks]
  .sort((a, b) => b.rating - a.rating)
  .slice(0, 8)

export const hiddenGemBooks = allBooks
  .filter((b) => (b.reviewCount ?? 0) < 100 && b.rating >= 4.3)
  .slice(0, 8)

export const getBooksByAuthor = (authorId: string) =>
  allBooks.filter((b) => b.authorId === authorId)

export const getBooksByTag = (tag: string) =>
  allBooks.filter((b) => b.tags?.includes(tag))

export const getBooksByGenre = (genre: string) =>
  allBooks.filter((b) =>
    b.genres.some((g) => g.toLowerCase() === genre.toLowerCase()),
  )

export const getBooksByAnthology = (anthologyId: string) =>
  allBooks.filter((b) => b.isAnthology && b.id === anthologyId)

export default allBooks
